/**
 * ThemeV2EditorPage - Full editor page for a single V2 theme
 *
 * Provides V2 ThemeSettingsPanel and ThemePreviewCarousel.
 * Uses OnboardingScreenEditorDialogV2 for individual screen editing.
 *
 * Key differences from V1 ThemeEditorPage:
 * - Uses ThemeV2 type and FlowDocument (not ScreensGraph)
 * - Screen editor uses OnboardingScreenEditorDialogV2 (StepNode-native)
 * - Import is for FlowDocument JSON (not ScreensGraph)
 * - Routes use /themes-v2/ prefix
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert as MuiAlert,
  TextField,
} from '@mui/material';
import { ArrowBack, Save, ContentCopy, FileUpload } from '@mui/icons-material';
import type { StepNode, FlowDocument } from '@segmently/schema-models/v2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { ThemeV2EditorView } from '../components/ThemeV2EditorView';
import { OnboardingScreenEditorDialogV2 } from '@modules/reactFlow/components/onboarding/OnboardingScreenEditorDialogV2';
import { useThemeV2 } from '../hooks/useThemeV2';
import { useThemeV2Actions } from '../hooks/useThemeV2Actions';
import { defaultThemeV2Mapper } from '../mappers/DefaultThemeV2Mapper';
import type { ThemeV2, UpdateThemeV2Input } from '../types/themesV2.types';

/**
 * Same clone convention as `themeV2EditorTransforms.ts` (`createEditableThemeV2FromDraft`,
 * `themeV2ToOnboardingSettings`): a JSON round-trip, which DROPS a key whose value is
 * `undefined` at any depth — unlike `structuredClone` (used elsewhere on this page for
 * local-state edits), which keeps such a key. `handleSave` needs the drop: see its
 * `animationSettings` snapshot below (interim review I-1).
 */
function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * ThemeV2EditorPage - Edit a single V2 theme
 */
export const ThemeV2EditorPage: React.FC = () => {
  const { themeId } = useParams<{ themeId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const projectId = useMemo(
    () => new URLSearchParams(location.search).get('projectId') ?? undefined,
    [location.search],
  );

  const { theme, loading, error, refresh } = useThemeV2(themeId || '');
  const { updateTheme, cloneTheme, loading: actionLoading } = useThemeV2Actions();

  // Local theme state for editing
  const [editedTheme, setEditedTheme] = useState<ThemeV2 | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Import confirmation: holds parsed FlowDocument until user decides
  const [pendingImport, setPendingImport] = useState<FlowDocument | null>(null);
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);

  // Key to force carousel refresh after screen save
  const [carouselKey, setCarouselKey] = useState(0);

  // File input ref for JSON import
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // V2 Screen editor dialog state
  const [screenEditorOpen, setScreenEditorOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<FlowStepNode | null>(null);

  // Ref for stable callback access (avoids stale closure)
  const stateRef = useRef<{
    handleChange: (path: string, value: unknown) => void;
    setCarouselKey: React.Dispatch<React.SetStateAction<number>>;
    setScreenEditorOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setEditingStep: React.Dispatch<React.SetStateAction<FlowStepNode | null>>;
  } | null>(null);

  // Initialize edited theme when loaded
  useEffect(() => {
    if (theme) {
      setEditedTheme(structuredClone(theme));
      setHasChanges(false);
    }
  }, [theme]);

  // Handle theme change from ThemeV2EditorView
  const handleThemeChange = useCallback((updatedTheme: ThemeV2) => {
    setEditedTheme(updatedTheme);
    setHasChanges(true);
  }, []);

  // Handle setting changes by path (for import and screen save)
  const handleChange = useCallback((path: string, value: unknown) => {
    setEditedTheme((prev) => {
      if (!prev) return prev;
      const updated = structuredClone(prev);

      const parts = path.split('.');
      let current: Record<string, unknown> = updated as unknown as Record<string, unknown>;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]] || typeof current[parts[i]] !== 'object') {
          current[parts[i]] = {};
        }
        current = current[parts[i]] as Record<string, unknown>;
      }
      current[parts[parts.length - 1]] = value;

      return updated;
    });
    setHasChanges(true);
  }, []);

  // Keep stateRef updated
  useEffect(() => {
    stateRef.current = {
      handleChange,
      setCarouselKey,
      setScreenEditorOpen,
      setEditingStep,
    };
  });

  // Save changes
  const handleSave = useCallback(async () => {
    if (!editedTheme || !themeId) return;

    try {
      const updates: UpdateThemeV2Input = {
        name: editedTheme.name,
        colors: editedTheme.colors,
        fonts: editedTheme.fonts,
        optionsListSettings: editedTheme.optionsListSettings,
        background: editedTheme.background,
        // Persist an explicit null so Firestore merge clears a previously
        // enabled Browser chrome color instead of retaining the old value.
        webViewBackgroundColor: editedTheme.webViewBackgroundColor ?? null,
        headerBar: editedTheme.headerBar,
        actionBar: editedTheme.actionBar,
        flowDocument: editedTheme.flowDocument,
        order: editedTheme.order,
        // Motion scheme (AMV-AC-004, I-1 fix): the complete current snapshot, JSON-cleaned
        // (cloneJson) so a reverted Direction ("Default") never sends a nested `undefined` —
        // the object structuredClone kept from local-state edits would otherwise make the
        // service's Firestore write throw synchronously. ThemeV2Service.update still replaces
        // it as a whole; undefined (legacy theme) leaves the stored value untouched.
        animationSettings: editedTheme.animationSettings
          ? cloneJson(editedTheme.animationSettings)
          : editedTheme.animationSettings,
      };

      await updateTheme(themeId, updates);
      setHasChanges(false);
      refresh();
    } catch (err) {
      // Owner rule: a refusal is never silent. Keep the edited state (hasChanges stays
      // true, editedTheme untouched) so the admin can retry after fixing the value.
      setNotification({
        open: true,
        message: `Failed to save theme: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error',
      });
    }
  }, [editedTheme, themeId, updateTheme, refresh]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (hasChanges) {
      setUnsavedChangesDialogOpen(true);
    } else {
      navigate(`/themes-v2${location.search}`);
    }
  }, [hasChanges, location.search, navigate]);

  // Confirm leaving without saving
  const handleConfirmLeave = useCallback(() => {
    setUnsavedChangesDialogOpen(false);
    navigate(`/themes-v2${location.search}`);
  }, [location.search, navigate]);

  // Cancel leaving
  const handleCancelLeave = useCallback(() => {
    setUnsavedChangesDialogOpen(false);
  }, []);

  // Clone theme
  const handleClone = useCallback(async () => {
    if (!themeId) return;

    try {
      const clonedId = await cloneTheme(themeId);
      navigate(`/themes-v2/${clonedId}${location.search}`);
    } catch {
      // Error is handled in hook
    }
  }, [themeId, cloneTheme, location.search, navigate]);

  // Open import dialog
  const handleImportClick = useCallback(() => {
    setImportDialogOpen(true);
  }, []);

  // Close import dialog
  const handleImportClose = useCallback(() => {
    setImportDialogOpen(false);
  }, []);

  // Trigger file picker
  const handleSelectFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle FlowDocument JSON file upload
  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      event.target.value = '';

      try {
        const content = await file.text();
        const data = JSON.parse(content);

        // Validation: screens must exist and be an object
        if (!data.screens || typeof data.screens !== 'object') {
          setNotification({
            open: true,
            message: 'Invalid JSON format: missing "screens" object',
            severity: 'error',
          });
          return;
        }

        // Validate each screen has screenType
        const screenIds = Object.keys(data.screens);
        for (const screenId of screenIds) {
          const screen = data.screens[screenId];
          if (!screen.screenType) {
            setNotification({
              open: true,
              message: `Invalid screen "${screenId}": missing "screenType" property`,
              severity: 'error',
            });
            return;
          }
        }

        // Build V2 FlowDocument from imported data
        const importedFlowDocument: FlowDocument = {
          schemaVersion: data.schemaVersion || '2.12.0',
          defaultLanguage: data.defaultLanguage || 'en-US',
          languages: data.languages || ['en-US'],
          launchScreenId: data.launchScreenId || screenIds[0] || '',
          screens: data.screens,
          metadata: data.metadata || {},
        };

        // Store parsed FlowDocument and show confirmation dialog
        setPendingImport(importedFlowDocument);
        setImportDialogOpen(false);
        setImportConfirmOpen(true);
      } catch (err) {
        setNotification({
          open: true,
          message: `Failed to parse JSON: ${(err as Error).message}`,
          severity: 'error',
        });
      }
    },
    []
  );

  // Close notification
  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  // Import confirmation: apply theme settings to imported screens
  const handleImportApplyTheme = useCallback(() => {
    if (!pendingImport) return;
    const screenCount = Object.keys(pendingImport.screens).length;

    setEditedTheme((prev) => {
      if (!prev) return prev;
      const updated = structuredClone(prev);
      updated.flowDocument = defaultThemeV2Mapper.applyToFlowDocument(updated, pendingImport);
      return updated;
    });
    setHasChanges(true);
    setImportConfirmOpen(false);
    setPendingImport(null);
    setNotification({
      open: true,
      message: `Imported ${screenCount} screen${screenCount !== 1 ? 's' : ''} with theme settings applied`,
      severity: 'success',
    });
  }, [pendingImport]);

  // Import confirmation: keep original colors/fonts from JSON
  const handleImportKeepOriginal = useCallback(() => {
    if (!pendingImport) return;
    const screenCount = Object.keys(pendingImport.screens).length;

    handleChange('flowDocument', pendingImport);
    setImportConfirmOpen(false);
    setPendingImport(null);
    setNotification({
      open: true,
      message: `Imported ${screenCount} screen${screenCount !== 1 ? 's' : ''} with original settings`,
      severity: 'success',
    });
  }, [pendingImport, handleChange]);

  // Import confirmation: cancel — discard imported data
  const handleImportCancel = useCallback(() => {
    setImportConfirmOpen(false);
    setPendingImport(null);
  }, []);

  // Screen edit — opens V2 editor dialog with FlowStepNode
  const handleScreenEdit = useCallback((screenId: string, step: StepNode) => {
    // Convert StepNode to FlowStepNode (adds entityId)
    const flowStep: FlowStepNode = {
      ...step,
      id: screenId,
      entityId: screenId,
    };
    setEditingStep(flowStep);
    setScreenEditorOpen(true);
  }, []);

  // Screen saved — update in theme's FlowDocument
  const handleStepSaved = useMemo(() => {
    return (step: FlowStepNode) => {
      if (!step?.id) return;

      const state = stateRef.current;
      if (!state) return;

      const { handleChange: change, setCarouselKey: setKey, setScreenEditorOpen: setOpen, setEditingStep: setStep } = state;

      // Update screen in theme.flowDocument.screens
      change(`flowDocument.screens.${step.id}`, step);

      // Force carousel refresh
      setKey(prev => prev + 1);
      setOpen(false);
      setStep(null);
    };
  }, []);

  // Close screen editor dialog
  const handleScreenEditorClose = useCallback(() => {
    setScreenEditorOpen(false);
    setEditingStep(null);
  }, []);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error || !editedTheme) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(`/themes-v2${location.search}`)} aria-label="Back to V2 themes">
            <ArrowBack />
          </IconButton>
          <Typography variant="h5">V2 Theme not found</Typography>
        </Box>
        <Alert severity="error">
          {error?.message || 'The requested V2 theme could not be found.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} aria-label="Back to V2 themes" data-testid="v2-theme-back-btn">
            <ArrowBack />
          </IconButton>
          <Typography variant="h5">{editedTheme.name}</Typography>
          <TextField
            label="Order"
            type="number"
            value={editedTheme.order ?? 0}
            onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
            size="small"
            sx={{ width: 100, ml: 2 }}
            slotProps={{ htmlInput: { min: 0 } }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileUpload />}
            onClick={handleImportClick}
            data-testid="v2-theme-import-flow-btn"
          >
            Import FlowDocument
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleClone}
            disabled={actionLoading}
            data-testid="v2-theme-clone-btn"
          >
            Clone
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={!hasChanges || actionLoading}
            data-testid="v2-theme-save-btn"
          >
            Save
          </Button>
        </Box>
      </Box>

      {/* Main content — V2 ThemeEditorView */}
      <Box sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
        <ThemeV2EditorView
          theme={editedTheme}
          onChange={handleThemeChange}
          onScreenEdit={handleScreenEdit}
          disabled={actionLoading}
          carouselKey={carouselKey}
          projectId={projectId}
        />
      </Box>

      {/* Unsaved changes dialog */}
      <Dialog open={unsavedChangesDialogOpen} onClose={handleCancelLeave}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to leave without saving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLeave}>Cancel</Button>
          <Button onClick={handleConfirmLeave} color="warning">
            Leave without saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importDialogOpen} onClose={handleImportClose} maxWidth="sm" fullWidth>
        <DialogTitle>Import FlowDocument</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Import a V2 FlowDocument from a JSON file. The file should contain a "screens" object
            with V2 StepNode definitions.
          </DialogContentText>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: 'action.hover',
            }}
          >
            <FileUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              Select a JSON file to import
            </Typography>
            <Button
              variant="contained"
              onClick={handleSelectFile}
              sx={{ mt: 1 }}
            >
              Select JSON File
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportClose}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input for JSON import */}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept=".json,application/json"
        onChange={handleFileUpload}
        data-testid="v2-import-file-input"
      />

      {/* Notification snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </MuiAlert>
      </Snackbar>

      {/* V2 Screen editor dialog */}
      <OnboardingScreenEditorDialogV2
        open={screenEditorOpen}
        onClose={handleScreenEditorClose}
        stepData={editingStep}
        onStepSaved={handleStepSaved}
        defaultLanguage={editedTheme?.flowDocument?.defaultLanguage || 'en-US'}
        languages={editedTheme?.flowDocument?.languages || ['en-US']}
        projectId="theme-v2-assets"
        onboardingId={themeId || ''}
        versionId="theme-v2-edit"
        flowDocument={editedTheme?.flowDocument}
      />

      {/* Import confirmation dialog — apply theme or keep original */}
      <Dialog open={importConfirmOpen} onClose={handleImportCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Apply Theme Settings?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The FlowDocument has been parsed successfully
            ({pendingImport ? Object.keys(pendingImport.screens).length : 0} screens).
            Would you like to apply the current theme settings (colors, fonts, background, header, action bar)
            to the imported screens?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportCancel}>Cancel</Button>
          <Button onClick={handleImportKeepOriginal} color="inherit">
            Keep Original
          </Button>
          <Button onClick={handleImportApplyTheme} variant="contained">
            Apply Theme
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ThemeV2EditorPage;
